/**
 * File Icon Component
 *
 * Shared component for displaying file/folder icons with appropriate colors
 * based on file type. Used by both CloudFileTree and CloudFileSelectModal.
 */

import React from "react";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import ImageIcon from "@mui/icons-material/Image";

// =============================================================================
// Icon Colors - Centralized for consistency
// =============================================================================

export const FILE_ICON_COLORS = {
  folder: "#f9a825",
  pdf: "#d32f2f",
  word: "#1976d2",
  excel: "#388e3c",
  image: "#7b1fa2",
  default: "#757575",
} as const;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the file extension from a filename.
 */
const getFileExtension = (filename: string): string => {
  if (!filename.includes(".")) return "";
  return "." + filename.split(".").pop()?.toLowerCase();
};

/**
 * Get the icon color for a given filename.
 * Useful when you need just the color (e.g., for matching caret colors).
 */
export const getFileIconColor = (
  filename: string,
  isDirectory: boolean,
): string => {
  if (isDirectory) {
    return FILE_ICON_COLORS.folder;
  }

  const ext = getFileExtension(filename).toLowerCase();

  switch (ext) {
    case ".pdf":
      return FILE_ICON_COLORS.pdf;
    case ".doc":
    case ".docx":
      return FILE_ICON_COLORS.word;
    case ".xls":
    case ".xlsx":
    case ".csv":
      return FILE_ICON_COLORS.excel;
    case ".jpg":
    case ".jpeg":
    case ".png":
    case ".gif":
    case ".svg":
      return FILE_ICON_COLORS.image;
    default:
      return FILE_ICON_COLORS.default;
  }
};

// =============================================================================
// Component
// =============================================================================

interface FileIconProps {
  /** The filename (used to determine file type) */
  filename: string;
  /** Whether this is a directory */
  isDirectory: boolean;
  /** Font size for the icon (default: inherit) */
  fontSize?: number | "small" | "medium" | "large" | "inherit";
  /** Additional sx props */
  sx?: Record<string, unknown>;
}

/**
 * Renders an appropriate icon for a file or folder based on its type.
 */
export const FileIcon: React.FC<FileIconProps> = ({
  filename,
  isDirectory,
  fontSize,
  sx = {},
}) => {
  const color = getFileIconColor(filename, isDirectory);
  const iconSx = { color, mr: 1, ...sx, ...(fontSize ? { fontSize } : {}) };

  if (isDirectory) {
    return <FolderIcon sx={iconSx} />;
  }

  const ext = getFileExtension(filename).toLowerCase();

  switch (ext) {
    case ".pdf":
      return <PictureAsPdfIcon sx={iconSx} />;
    case ".doc":
    case ".docx":
      return <DescriptionIcon sx={iconSx} />;
    case ".xls":
    case ".xlsx":
    case ".csv":
      return <TableChartIcon sx={iconSx} />;
    case ".jpg":
    case ".jpeg":
    case ".png":
    case ".gif":
    case ".svg":
      return <ImageIcon sx={iconSx} />;
    default:
      return <InsertDriveFileIcon sx={iconSx} />;
  }
};

export default FileIcon;
